import React, {Component} from 'react';
import {connect} from 'react-redux';
import {Link} from 'react-router-dom';
import axios from 'axios';
import {passCsrfToken, toTableData} from '../../helpers';
import swal from 'sweetalert';
import {confirmPartner, showPartner} from '../../api/partnerApi';
import {getSummary} from '../../api/serviceTransactionApi';
import {formatMoneyLegacy} from '../../utils/currency';
import {Badge, Sidebar} from '../shared';
import {PersonalProfile, CompanyProfile} from './PartnerProfile';
import Services from './PackageServiceDetails';
import RentalBooking from './RentalBookings';
import PackageBookings from './PackageBookings';
import PartnerProfile from './PartnerProfile';
import { SimpleStatusLegend } from '../admin/AdminStatusLegend';

class Dashboard extends Component {
	constructor(props) {
		super(props);
		this.state = {
			partner: {
				vendor_services: [],
				car_bookings: [],
				service_transactions: []
			},
			balance: null
		};
	}

	componentDidMount() {
		passCsrfToken(document, axios);
		this.setState({ partner: { vendor_services: [], car_bookings: [], service_transactions: [] } });
		if (this.props.currentUser && this.props.currentUser.vendor) {
			this.fetchBalance();
		}
	}

	componentDidUpdate(prevProps, prevState) {
		if (this.props.currentUser && this.props.currentUser.email != null && prevState.partner.id == null) {
			this.fetchPartner();
		}
		if (this.props.currentUser && this.props.currentUser.partner && this.state.partner.id != null && this.state.balance == null) {
			this.fetchBalance();
		}
	}

	fetchBalance = () => {
		getSummary()
			.then((res) => {
				const list = Array.isArray(res.data) ? res.data : (res.data && res.data.summaries) || [];
				this.setState({ balance: list[0] || null });
			})
			.catch(() => this.setState({ balance: null }));
	};

	fetchPartner = () => {
		showPartner(this.props.currentUser.vendor.idx)
			.then((response) => {
				this.setState({ partner: response.data });
				this.fetchBalance();
			})
			.catch((error) => console.log(' partner fetch error', error));
	};

	render() {
		const section = this.props.location.hash;
		const {partner, balance} = this.state;
		const showDashboard = section === '' || section === '#packages' || section === '#rentals' || section === '#transactions';
		const userName = (this.props.currentUser && this.props.currentUser.name) || 'Vendor';
		// Per-currency: by_currency is { USD: { credit, debit, due }, NPR: { ... } }. Fallback to flat credit/debit/due as NPR.
		const byCurrency = balance && balance.by_currency && Object.keys(balance.by_currency).length > 0
			? balance.by_currency
			: (balance && (balance.credit != null || balance.debit != null || balance.due != null)
				? { NPR: { credit: balance.credit, debit: balance.debit, due: balance.due } }
				: null);
		const currencyCodes = byCurrency ? Object.keys(byCurrency).sort() : [];

		return (
			<div className="vd">
				<div className="dashboard container">
					{showDashboard && (
						<>
							{/* Welcome */}
							<div className="vd-welcome">
								<div className="vd-welcome__icon" aria-hidden="true">
									<i className="fas fa-user-circle" />
								</div>
								<div className="vd-welcome__text">
									<h1>Hi, {userName}</h1>
									<p>Your partner space — earnings, assignments, and payment history in one place.</p>
								</div>
							</div>

							{/* Your money — per currency */}
							<h2 className="vd-earnings-title">Your money</h2>
							<SimpleStatusLegend title="Payout status on your trips" />
							<p className="vd-earnings-desc">Total earned updates after admin records payment. Balance due is what you are still owed.</p>
							{currencyCodes.length === 0 && (
								<div className="vd-stats">
									<div className="vd-stat vd-stat--earned">
										<div className="vd-stat__value">—</div>
										<div className="vd-stat__hint">No payouts yet</div>
									</div>
								</div>
							)}
							{currencyCodes.map((cc) => {
								const row = byCurrency[cc];
								const credit = row.credit != null ? Number(row.credit) : 0;
								const debit = row.debit != null ? Number(row.debit) : 0;
								const due = row.due != null ? Number(row.due) : (credit - debit);
								const label = cc === 'USD' ? 'USD' : cc === 'NPR' ? 'NPR (Rs)' : cc;
								return (
									<div key={cc} className="mb-3">
										<h3 className="h6 text-muted mb-2">{label}</h3>
										<div className="vd-stats">
											<div className="vd-stat vd-stat--earned">
												<div className="vd-stat__icon"><i className="fas fa-chart-line" /></div>
												<div className="vd-stat__label">Total earned</div>
												<div className="vd-stat__value">{formatMoneyLegacy(credit, cc)}</div>
												<div className="vd-stat__hint">Earned (credits) for this currency</div>
											</div>
											<div className="vd-stat vd-stat--paid">
												<div className="vd-stat__icon"><i className="fas fa-check-circle" /></div>
												<div className="vd-stat__label">Paid to you</div>
												<div className="vd-stat__value">{formatMoneyLegacy(debit, cc)}</div>
												<div className="vd-stat__hint">Already received</div>
											</div>
											<div className="vd-stat vd-stat--due">
												<div className="vd-stat__icon"><i className="fas fa-wallet" /></div>
												<div className="vd-stat__label">Balance due</div>
												<div className="vd-stat__value">{formatMoneyLegacy(due, cc)}</div>
												<div className="vd-stat__hint">Outstanding</div>
											</div>
										</div>
									</div>
								);
							})}
							{currencyCodes.includes('USD') && (
								<p className="text-muted small mt-2 mb-0">
									USD earnings will be settled in NPR internally based on your agreed conversion.
								</p>
							)}
							<div className="alert alert-info vd-payment-flow mt-3 mb-0 small">
								<strong>Simple flow:</strong> Finish the trip → click <strong>Mark complete</strong> → admin records payment → status becomes <strong>Paid</strong> and the amount appears under <strong>Paid to you</strong>.
							</div>

							{/* Assignments & history */}
							<h2 className="vd-assignments-title">Assignments &amp; history</h2>
							<p className="vd-assignments-desc">Active assignments and your payment history.</p>
							<div className="vd-tiles">
								<Link to="/partner/package_bookings" className="vd-tile">
									<div className="vd-tile__icon"><i className="fas fa-briefcase" /></div>
									<div className="vd-tile__count">{partner.active_vendor_services_count ?? partner.vendor_services?.length ?? 0}</div>
									<div className="vd-tile__label">Active private tours</div>
									<span className="vd-tile__action">View details <i className="fas fa-arrow-right" /></span>
								</Link>
								<Link to="/partner/car_bookings" className="vd-tile">
									<div className="vd-tile__icon"><i className="fas fa-car-side" /></div>
									<div className="vd-tile__count">{partner.active_car_bookings_count ?? partner.car_bookings?.length ?? 0}</div>
									<div className="vd-tile__label">Active rentals</div>
									<span className="vd-tile__action">View details <i className="fas fa-arrow-right" /></span>
								</Link>
								<Link to="/partner/group_tours" className="vd-tile">
									<div className="vd-tile__icon"><i className="fas fa-route" /></div>
									<div className="vd-tile__count">{partner.active_group_tours_count ?? 0}</div>
									<div className="vd-tile__label">Active group trips</div>
									<span className="vd-tile__action">View details <i className="fas fa-arrow-right" /></span>
								</Link>
								<Link to="/partner/service_transactions" className="vd-tile">
									<div className="vd-tile__icon"><i className="fas fa-receipt" /></div>
									<div className="vd-tile__count">{partner.service_transactions_count ?? partner.service_transactions?.length ?? 0}</div>
									<div className="vd-tile__label">Payment history</div>
									<span className="vd-tile__action">View details <i className="fas fa-arrow-right" /></span>
								</Link>
							</div>
						</>
					)}
					{section === '#profile' && (
						<div className="ui segment">
							<PartnerProfile partner={partner} />
						</div>
					)}
				</div>
			</div>
		);
	}
}

const mapStateToProps = ({userStore}) => ({
	currentUser: userStore.currentUser
});

const mapDispatchToProps = {};

export default connect(mapStateToProps, mapDispatchToProps)(Dashboard);
